import { setup, assign } from "xstate";
import type { Position } from "@engine/types/grid.js";

interface EnemyContext {
  instanceId: string;
  position: Position;
  hp: number;
  maxHp: number;
  patrolPath: Position[];
  patrolIndex: number;
}

type EnemyEvent =
  | { type: "PLAYER_DETECTED" }
  | { type: "PLAYER_LOST" }
  | { type: "NOISE_HEARD" }
  | { type: "ALERTED_TIMEOUT" }
  | { type: "ALERT_TIMEOUT" }
  | { type: "MOVE"; target: Position }
  | { type: "ANIMATION_COMPLETE" }
  | { type: "ATTACK_COMMAND" }
  | { type: "TAKE_DAMAGE"; amount: number };

/**
 * E04 — `ALERTED` / `ALERT` states.
 *
 * Full enemy lifecycle per `specs/enemies-and-ai.md`:
 *
 * ```
 *   IDLE ↔ PATROLLING
 *     ↓         ↓
 *   NOISE_HEARD → ALERTED ─ PLAYER_DETECTED → COMBAT ↔ ATTACKING
 *     ↑                                         ↓
 *     └── ALERT_TIMEOUT ── ALERT ← PLAYER_LOST ──┘
 * ```
 *
 * - **ALERTED** (pre-combat): heard something but hasn't seen the target
 *   — held until `PLAYER_DETECTED` (confirmed sight) or
 *   `ALERTED_TIMEOUT` (noise faded, no contact) which returns to IDLE.
 * - **ALERT** (post-combat): lost the target after engagement — returns
 *   to IDLE on `ALERT_TIMEOUT`, or snaps back to COMBAT if the player is
 *   re-detected (`PLAYER_DETECTED`).
 *
 * Timeouts are driven by the caller (CombatManager / noise broadcast),
 * not by XState after-delays — this keeps the machine deterministic and
 * world-freeze friendly.
 */
export const enemyMachine = setup({
  types: {
    context: {} as EnemyContext,
    events: {} as EnemyEvent,
    input: {} as EnemyContext,
  },
}).createMachine({
  id: "enemy",
  initial: "idle",
  context: ({ input }) => input,
  states: {
    idle: {
      on: {
        PLAYER_DETECTED: "combat",
        NOISE_HEARD: "alerted",
        MOVE: { target: "patrolling", actions: assign({ position: ({ event }) => event.target }) },
      },
    },
    patrolling: {
      on: {
        PLAYER_DETECTED: "combat",
        NOISE_HEARD: "alerted",
        ANIMATION_COMPLETE: "idle",
      },
    },
    alerted: {
      on: {
        PLAYER_DETECTED: "combat",
        ALERTED_TIMEOUT: "idle",
      },
    },
    combat: {
      on: {
        PLAYER_LOST: "alert",
        ATTACK_COMMAND: "attacking",
        TAKE_DAMAGE: {
          actions: assign({
            hp: ({ context, event }) => Math.max(0, context.hp - event.amount),
          }),
          target: "checkDead",
        },
      },
    },
    attacking: {
      // Blocks new ATTACK_COMMAND until animation resolves
      on: {
        ANIMATION_COMPLETE: "combat",
        TAKE_DAMAGE: {
          actions: assign({
            hp: ({ context, event }) => Math.max(0, context.hp - event.amount),
          }),
          target: "checkDead",
        },
      },
    },
    alert: {
      on: {
        PLAYER_DETECTED: "combat",
        NOISE_HEARD: "alerted",
        ALERT_TIMEOUT: "idle",
      },
    },
    checkDead: {
      always: [
        { guard: ({ context }) => context.hp <= 0, target: "dead" },
        { target: "combat" },
      ],
    },
    dead: {
      type: "final",
    },
  },
});
